import { CartLinesUpdateEvent, CartErrorEvent } from '@theme/standard-events';

class ProductFormComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.cartNotification = document.querySelector('cart-notification');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.isCartPage = !!document.body.classList.contains('template-cart');
    this.isInsideCartDrawer = !!this.closest('cart-drawer');

    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this.onSubmitHandler);
  }

  onSubmitHandler(event, trigger = null) {
    if (event) event.preventDefault();

    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButton.setAttribute('disabled', true);
    this.submitButton.classList.add('button--loading');
    const addToCartBehavior = this.submitButton.dataset.addToCartBehavior;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);
    config.body = formData;

    // Shopify standard event, dispatched before the fetch; the cart section renderer
    // listens for it and re-renders all [data-cart-section] markers when it settles
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: this.isInsideCartDrawer ? 'dialog' : this.isCartPage ? 'cart' : 'product',
        lines: [
          {
            merchandiseId: formData.get('id'),
            quantity: Number(formData.get('quantity')) || 1,
          },
        ],
        promise: deferred.promise,
      }),
    );

    fetch(`${routes.cart_add_url}`, config)
      .then(response => response.json())
      .then((parsedState) => {
        if (parsedState.status == 422) {
          // When quantity error
          this.cartNotification?.renderQuantityError(formData.get('inventory_quantity'), this.submitButton);

          this.dispatchEvent(
            new CartErrorEvent({
              error: parsedState.description || window.cartStrings.error,
              code: 'INVALID',
            }),
          );

          // Items could still have been added despite the error; the rejection makes
          // the cart section renderer re-sync and re-render the cart state
          deferred.reject(new Error(parsedState.description || 'Add to cart failed'));
          return;
        }

        // Resolve with the updated cart; this hands rendering over to the cart section
        // renderer. Note: not Shopify.actions.getCart() — its cart shape does not pass
        // the standard-events payload validation (lines must be a flat array), so we use
        // the documented AJAX pattern with createCartFromAjaxResponse.
        fetch(`${routes.cart_url}.js`)
          .then(response => response.json())
          .then((ajaxCart) => {
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            });
          })
          .catch(error => deferred.reject(error));

        // Inside the drawer there is nothing to open or announce; the renderer updates the contents
        if (this.isInsideCartDrawer) return;

        // for quick-shop triggers
        if (this.closest('dialog')) {
          this.submitButton = this.closest('dialog').querySelector('[command="show-modal"]');
        }

        switch (addToCartBehavior) {
        case 'open_cart_drawer':
          // The dialog element itself is never re-rendered, so it can open right away
          // while the renderer swaps the contents in. The cart promise rides along so
          // the drawer's cart:view event reuses it instead of fetching the cart again.
          this.dispatchCustomEvent(this.cartDrawer, 'cart-drawer:open', {
            trigger: trigger || this.submitButton,
            cartPromise: deferred.promise,
          });
          break;
        case 'show_cart_notification':
          if (!this.isCartPage && !document.body.classList.contains('cart-drawer-is-open')) {
            this.cartNotification?.show({
              addedVariantId: parsedState.id,
              trigger: this.submitButton,
            });
          }
          break;
        default:
          this.cartNotification?.show({
            addedVariantId: parsedState.id,
            trigger: this.submitButton,
          });
        }
      })
      .catch((error) => {
        debug() && console.error(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message,
            code: 'SERVICE_UNAVAILABLE',
          }),
        );
        deferred.reject(error);
      })
      .finally(() => {
        this.submitButton?.classList.remove('button--loading');
        this.submitButton?.removeAttribute('disabled');
      });
  }

  dispatchCustomEvent(element, eventName, detail) {
    if (!element) return;

    element.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: detail,
      }),
    );
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
